import { MutableRefObject, useCallback, useEffect } from "react";

import { find, isNil } from "lodash/fp";
import { useSearchParams } from "react-router-dom";

import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useTranscriptStore from "@app/analytics/MeetingReport/stores/transcript";
import useMeetingReportUiStore from "@app/analytics/MeetingReport/stores/ui";
import useVideoStore from "@app/analytics/MeetingReport/stores/video";
import type { MeetingReportTab } from "@app/analytics/MeetingReport/types";
import { waitForScrollEnd } from "@app/analytics/utils";
import useDebounce from "@app/common/hooks/useDebounce";
import {
  elementIsVisible,
  getElementPosition,
} from "@app/common/hooks/useVisibility";

const MAIN_CONTENT_VISIBILITY_OPTS = { topOffset: 90 };
const SIDEBAR_VISIBILITY_OPTS = { topOffset: 0 };

const useTranscriptAutoFollow = ({
  context,
  scrollContainer,
}: {
  context: { section: "main" | "sidebar"; tab: MeetingReportTab };
  scrollContainer: MutableRefObject<HTMLDivElement | null>;
}) => {
  const [queryParams] = useSearchParams();

  const visibilityOpts =
    context.section === "main"
      ? MAIN_CONTENT_VISIBILITY_OPTS
      : SIDEBAR_VISIBILITY_OPTS;

  const setNudge = useMeetingReportUiStore((state) => state.setNudge);
  const { playbackEnabled, trailerActive } = useVideoStore((state) => ({
    playbackEnabled: state.playbackEnabled,
    trailerActive: state.trailer.active,
  }));

  const inEditMode = useEditStore((state) => state.editingSection != null);

  const {
    setAutoFollow,
    playbackParagraphId,
    getPlaybackParagraphIndex,
    transcriptItems,
  } = useTranscriptStore((state) => ({
    setAutoFollow: state.setAutoFollow,
    playbackParagraphId: state.playbackParagraphId,
    getPlaybackParagraphIndex: state.getPlaybackParagraphIndex,
    transcriptItems:
      context.section === "sidebar"
        ? state.transcriptItems
        : state.filteredTranscriptItems,
  }));

  const inTargetedSection =
    queryParams.get("section") === "transcript" ||
    context.section === "sidebar";

  // the main content is the only place we (currently) support speaker filtering, so unless that's
  // where we are, assume that the playback turn is available to be targeted
  const isPlaybackTurnAvailable =
    context.section !== "main" ||
    (playbackParagraphId != null &&
      Boolean(find({ id: playbackParagraphId }, transcriptItems)));

  const disableListeners =
    !playbackEnabled || inEditMode || !inTargetedSection || trailerActive;

  const triggerInitialToggle =
    inTargetedSection && !trailerActive && playbackParagraphId != null;

  const clearNudge = () => setNudge(context.section, null);

  const toggleAutoFollow = useCallback(
    (onOnly = false) => {
      if (!scrollContainer.current) return;

      if (!playbackParagraphId || !isPlaybackTurnAvailable || inEditMode) {
        clearNudge();
        setAutoFollow(false);
        return;
      }

      const playbackTurnRef = document.getElementById(playbackParagraphId);

      const currentTurnIsVisible = elementIsVisible(
        playbackTurnRef,
        scrollContainer.current,
        visibilityOpts
      );

      // toggle auto-follow based on if the current turn is visible. this includes:
      // - disabling auto-follow when the user scrolls away from the current turn
      // - enabling auto-follow when the user scrolls back to the current turn OR the turn comes into
      //   view via catching up with the current scroll position
      // when "onOnly" is true, this function will not disable auto-follow if the element exits the view.
      // this will allow a turn that just became active but is partially or completely out of view to
      // scroll into view without briefly disabling auto-follow
      if (!onOnly || (onOnly && currentTurnIsVisible)) {
        setAutoFollow(currentTurnIsVisible);
      }

      // update nudge based on current turn's visibility (nudge should only show when auto follow is disabled)
      if (currentTurnIsVisible) {
        clearNudge();
      } else if (!onOnly && playbackTurnRef) {
        const { top: elementTop } = getElementPosition(playbackTurnRef);
        const { top: containerTop } = getElementPosition(
          scrollContainer.current,
          visibilityOpts
        );

        const placement = elementTop < containerTop ? "top" : "bottom";
        setNudge(context.section, {
          placement,
          label: "Jump to current position",
          tab: context.tab,
        });
      } else if (!onOnly) {
        // Handle when element is not rendered in the list
        // Here we find the first visible element and compare its index to the playback turn index
        const transcriptContainer = window.document.getElementById(
          `transcript-virtuoso-${context.section}`
        );

        if (!transcriptContainer) {
          setNudge(context.section, {
            placement: "bottom",
            label: "Jump to current position",
            tab: context.tab,
          });
          return;
        }

        const playbackParagraphIndex = getPlaybackParagraphIndex();
        const transcriptItem =
          transcriptContainer.querySelector("[data-item-index]");
        const transcriptItemIndex =
          transcriptItem?.getAttribute("data-item-index");

        const transcriptItemExists =
          transcriptItem && !isNil(transcriptItemIndex);
        const placement =
          transcriptItemExists &&
          playbackParagraphIndex < parseInt(transcriptItemIndex)
            ? "top"
            : "bottom";

        setTimeout(() => {
          const playbackTurnRef = document.getElementById(playbackParagraphId);
          if (playbackTurnRef) return;

          setNudge(context.section, {
            placement,
            label: "Jump to current position",
            tab: context.tab,
          });
        }, 1000);
      }
    },
    [
      scrollContainer.current,
      playbackParagraphId,
      getPlaybackParagraphIndex,
      context.section,
      isPlaybackTurnAvailable,
      inEditMode,
    ]
  );

  const debouncedToggleAutoFollow = useDebounce(toggleAutoFollow, 5);

  useEffect(() => {
    if (context.section !== "main") return;

    // update the nudge when the transcript filters are being updated
    toggleAutoFollow();
  }, [toggleAutoFollow, context.section, transcriptItems.length]);

  // disable auto-follow as soon as user leaves the targeted section
  useEffect(() => {
    if (inTargetedSection || !playbackEnabled) return;

    setAutoFollow(false);

    if (playbackParagraphId) {
      setNudge(context.section, {
        placement: "bottom",
        label: "Jump to current position",
        tab: context.tab,
      });
    }
  }, [inTargetedSection, playbackEnabled, playbackParagraphId]);

  // enable auto follow if the turn is visible when it becomes active
  useEffect(() => {
    if (disableListeners) return;

    toggleAutoFollow(true);
  }, [disableListeners, toggleAutoFollow]);

  // toggle auto follow if this is the first playback turn (already on transcript section),
  // and it's not currently in view
  useEffect(() => {
    if (triggerInitialToggle && playbackEnabled) {
      toggleAutoFollow();
    }
  }, [triggerInitialToggle]);

  const scrollListener = useCallback(() => {
    waitForScrollEnd(scrollContainer.current).then(() => {
      debouncedToggleAutoFollow();
    });
  }, [scrollContainer.current, debouncedToggleAutoFollow]);

  const debouncedScrollListener = useDebounce(scrollListener, 5);

  // enable or disable auto follow based on the turn's visibility after the
  // scroll completes
  useEffect(() => {
    if (disableListeners) {
      scrollContainer.current?.removeEventListener(
        "scroll",
        debouncedScrollListener
      );
      return;
    }

    scrollContainer.current?.addEventListener(
      "scroll",
      debouncedScrollListener
    );

    return () =>
      scrollContainer.current?.removeEventListener(
        "scroll",
        debouncedScrollListener
      );
  }, [disableListeners, debouncedScrollListener]);

  useEffect(() => {
    // hide nudge and disable auto-follow on unmount
    return () => {
      clearNudge();
      setAutoFollow(false);
    };
  }, []);
};

export default useTranscriptAutoFollow;
